'use client'

import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import { useTheme } from './ThemeProvider'

interface HeaderProps {
  currentPage?: 'home' | 'work' | 'model' | 'product' | 'profile'
}

export default function Header({ currentPage }: HeaderProps) {
  const [showNav, setShowNav] = useState(false)
  const { theme, setTheme } = useTheme()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (showNav && !target.closest('.logo-dropdown-container')) {
        setShowNav(false)
      }
    }

    if (showNav) {
      document.addEventListener('click', handleClickOutside)
    }

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [showNav])

  return (
    <div className="header-container" 
         style={{
           backgroundColor: currentPage === 'home' 
             ? 'transparent' 
             : theme === 'dark' ? 'rgba(49, 47, 54, 1)' : 'rgba(242, 240, 239, 1)'
         }}
    >
      {/* Logo Column - Left */}
      <div className="header-logo">
        <div 
          className="group pointer-events-auto leading-snug relative logo-dropdown-container"
          onMouseEnter={() => setShowNav(true)}
          onMouseLeave={() => setShowNav(false)}
          onClick={() => {
            // On mobile, toggle dropdown on click since there's no hover
            if (window.innerWidth < 768) {
              setShowNav(!showNav);
            }
          }}
        >
          <Link 
            href="/" 
            className="block w-fit focus:outline-none"
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              // On mobile, prevent navigation when dropdown is open
              if (window.innerWidth < 768 && showNav) {
                e.preventDefault();
              }
            }}
          >
            <span className={`block tracking-logo select-none uppercase transition-colors ${
              theme === 'dark' ? 'text-white' : 'text-primary'
            }`}>NOLAN DANIEL WHITE</span>
          </Link>
          
          {/* Dropdown Menu */}
          {showNav && (
            <div className={`absolute top-full left-0 py-0 min-w-[120px] transition-colors z-50 ${
              theme === 'dark' ? 'text-white' : 'text-primary'
            }`} style={{
              '--bg-color': theme === 'dark' ? '#000000' : '#ffffff'
            } as React.CSSProperties}>
              <Link href="/work" className={`logo-dropdown-item block py-0 hover:text-secondary transition-colors leading-tight focus:outline-none pl-0 pr-1 ${
                theme === 'dark' ? 'text-white hover:text-gray-300' : 'text-black hover:text-gray-600'
              }`} style={{
                backgroundColor: theme === 'dark' ? '#312F36' : '#f2f0ef'
              }}>
                WORK
              </Link>
              <Link href="/model" className={`logo-dropdown-item block py-0 hover:text-secondary transition-colors leading-tight focus:outline-none pl-0 pr-1 ${
                theme === 'dark' ? 'text-white hover:text-gray-300' : 'text-black hover:text-gray-600'
              }`} style={{
                backgroundColor: theme === 'dark' ? '#312F36' : '#f2f0ef'
              }}>
                MODEL
              </Link>
              <Link href="/product" className={`logo-dropdown-item block py-0 hover:text-secondary transition-colors leading-tight focus:outline-none pl-0 pr-1 ${
                theme === 'dark' ? 'text-white hover:text-gray-300' : 'text-black hover:text-gray-600'
              }`} style={{
                backgroundColor: theme === 'dark' ? '#312F36' : '#f2f0ef'
              }}>
                PRODUCT
              </Link>
              <Link href="/profile" className={`logo-dropdown-item block py-0 hover:text-secondary transition-colors leading-tight focus:outline-none pl-0 pr-1 ${
                theme === 'dark' ? 'text-white hover:text-gray-300' : 'text-black hover:text-gray-600'
              }`} style={{
                backgroundColor: theme === 'dark' ? '#312F36' : '#f2f0ef'
              }}>
                PROFILE
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Right side toggles */}
      <div className="header-toggles">
        {/* Day/Night Mode Toggle - Desktop only for non-home pages */}
        {currentPage !== 'home' && (
          <button
            onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
            className={`hidden md:block text-xs uppercase tracking-widest hover:opacity-70 transition-opacity cursor-pointer focus:outline-none ${
              theme === 'dark' ? 'text-white' : 'text-primary'
            }`}
          >
            {theme === 'light' ? 'NIGHT' : 'DAY'}
          </button>
        )}
        
        {/* Mobile Theme Toggle - For non-home pages except work */}
        {currentPage !== 'home' && currentPage !== 'work' && (
          <button
            onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
            className={`md:hidden text-xs uppercase tracking-widest hover:opacity-70 transition-opacity cursor-pointer focus:outline-none ${
              theme === 'dark' ? 'text-white' : 'text-primary'
            }`}
          >
            {theme === 'light' ? 'NIGHT' : 'DAY'}
          </button>
        )}
        
        {/* Additional toggle buttons can be passed as children or props */}
      </div>

      {/* Homepage Theme Toggle - Positioned in left container area */}
      {currentPage === 'home' && (
        <div className="absolute top-0 right-0 md:top-1/2 md:right-2 md:transform md:-translate-y-1/2 z-50 pointer-events-auto">
          <button
            onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
            className={`text-xs uppercase tracking-widest px-1.5 py-0.5 focus:outline-none transition-colors duration-300 ${
              theme === 'dark' 
                ? 'bg-dark text-white' 
                : 'bg-day text-black'
            }`}
            style={{ 
              fontSize: '0.75rem', 
              lineHeight: '1',
              WebkitTapHighlightColor: 'transparent',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              userSelect: 'none',
              right: '0px !important',
              top: '0px !important',
              zIndex: 9999
            }}
          >
            {theme === 'light' ? 'NIGHT' : 'DAY'}
          </button>
        </div>
      )}
    </div>
  )
}
